import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { MdRefresh } from "react-icons/md";
import { useInView } from "react-intersection-observer";
import ClipLoader from "react-spinners/ClipLoader";
import { darkNoTransaction, noTransaction } from "../../../assets/images";
import { API } from "../../../constants/api";
import { useUser } from "../../../context/user-context";
import useAuthAxios from "../../../utils/baseAxios";
import { errorMessage } from "../../../utils/errorMessage";
import { formatDateWithSuffix } from "../../../utils/formatTime";
import ListDetailsModal from "./ListDetailsModal";

const Lists = () => {
  const axiosInstance = useAuthAxios();
  const { theme } = useUser();
  const [lists, setLists] = useState<any[]>([]);
  const [selectedList, setSelectedList] = useState<any>(null);

  const [page, setPage] = useState<number>(1);
  const divRef = useRef<HTMLDivElement | null>(null);
  const [ref, inView] = useInView();
  const getPoint = async () => {
    const response = await axiosInstance.get(API.getPointsActivity);
    return response.data;
  };
  const {
    data: pointActivity,
    error: error1,
    isLoading,
  } = useQuery({
    queryKey: ["get-point-activity", page],
    queryFn: getPoint,
    retry: 1,
  });
  useEffect(() => {
    if (error1) {
      const newError = error1 as any;
      toast.error(errorMessage(newError?.message || newError?.data?.message));
    }
  }, [error1]);
  const activityList = pointActivity?.data?.activities;
  const totalPages = pointActivity?.data.meta.totalPages || 0;

  useEffect(() => {
    if (inView) {
      if (page < totalPages) {
        setPage((prev) => prev + 1);
      }
    }
  }, [inView, totalPages]);
  useEffect(() => {
    if (activityList) {
      const newActivity = [...lists, ...activityList];

      // Remove duplicates (prefer _id if available, else timestamp)
      const uniqueActivity = Array.from(
        new Map(
          newActivity.map((act) => [act._id ?? act.timestamp, act])
        ).values()
      );

      // Newest first
      uniqueActivity.sort(
        (a, b) =>
          new Date(b.timestamp ?? 0).getTime() -
          new Date(a.timestamp ?? 0).getTime()
      );

      setLists(uniqueActivity);
    }
  }, [activityList]);

  const handleListClick = (list: any) => {
    setSelectedList(list);
  };

  const handleCloseModal = () => {
    setSelectedList(null);
  };

  return (
    <>
      <div className="bg-transparent rounded-xl  mt-4 h-[450px] overflow-auto">
        <div ref={divRef} className="w-full h-[450px]">
          {lists.length >= 1 ? (
            <>
              <div className="w-full flex flex-col gap-3">
                {lists.map((list: any, index: any) => (
                  <div
                    key={index}
                    className="flex justify-between gap-3 items-start cursor-pointer rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2227] p-4 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    onClick={() => handleListClick(list)}
                  >
                    <div className="flex justify-start items-center gap-3 min-w-0">
                      <div className="min-w-0">
                        <h4
                          className="text-gray-900 dark:text-gray-50 font-semibold text-[14px] truncate max-w-full"
                          title={list.message || list.source}
                        >
                          {list.message || list.source}
                        </h4>
                        <div className="flex flex-wrap items-center gap-2 mt-1.5">
                          <span className="text-gray-500 dark:text-gray-400 text-[12px]">
                            {formatDateWithSuffix(list.timestamp)}
                          </span>
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium text-[#03C668] bg-[#03C668]/20">
                            Completed
                          </span>
                        </div>
                      </div>
                    </div>
                    <h4 className="text-gray-900 dark:text-gray-100 font-semibold text-[14px] shrink-0">
                      {list.points} points
                    </h4>
                  </div>
                ))}
                <div ref={ref} className=""></div>
              </div>
            </>
          ) : (
            !isLoading && (
              <div className="flex flex-col justify-center mt-16 lgss:mt-24 items-center">
                {theme === "dark" ? (
                  <img src={darkNoTransaction} alt="" />
                ) : (
                  <img src={noTransaction} alt="" />
                )}
                <h4 className="font-bold text-[#869AA9] pt-2">No activity</h4>
                <p className="text-[#869AA9] pt-2 text-[12px]">
                  All your point activities will show up here
                </p>
              </div>
            )
          )}
          {isLoading && (
            <div className="mt-6 flex justify-center items-center">
              <ClipLoader color="#0B75C2" size={60} />
            </div>
          )}
        </div>
      </div>

      {selectedList && (
        <ListDetailsModal list={selectedList} onClose={handleCloseModal} />
      )}
    </>
  );
};

export default Lists;
